import { getBackendUrl } from "@Utils";
import { useTranslation } from "@i18n";
import { useEffect, useState } from "react";
import type { ChangeEvent } from "react";
import IconComponent from "@CubeComponents/icons";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import DesignViewerLayoutSettingComponent from "./SettingComponent";
import axiosWrapper from "@Services/axiosWrapper";
import type { LayoutData, UpdateViewResponse } from "@ServerTypes/layoutView/layoutView.schema";
import type { SidebarGroup } from "./SettingComponent";

import Accordion from "@CubeComponents/html/accordion/materialUiAccordian/index";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/** An rc-dock node as persisted in the layout blob: a box with children, or a panel with tabs. */
export type RcDockNode = { children?: RcDockNode[]; tabs?: { id: string }[]; activeId?: string };

/** rc-dock's `LayoutBase`: the dockbox / floatbox / windowbox / maxbox keys, each a box of nodes. */
export type RcDockLayout = Record<string, { children: RcDockNode[] }>;

/**
 * SCHEMA GAP: `layoutView`'s `layoutDataSchema.data` is the opaque `Record<string, unknown>` blob,
 * so the VIEWER resourceType's own shape is undeclared server-side. This is what design-viewer
 * writes (DesignViewerLayoutManager's `createNewLayoutData`) and reads back. `zoomLevel` is
 * nullable because `onSave` below persists the component state, which starts as `null`.
 */
export type ViewerLayoutBlob = {
  sidebarLayout: { addedData: string[] };
  zoomLevel: number | null;
  rcDock: RcDockLayout;
};

/** A saved VIEWER layout row, plus the pane index DesignViewerLayoutManager stamps while editing. */
export type ViewerLayout = Omit<LayoutData, "data"> & { data: ViewerLayoutBlob; layoutDataIndex?: number };

/**
 * VIEWER-CORE GAP: the viewer shell hands DesignViewer `{ rcDockref, setDockLayoutfnc }`, and
 * viewer-core's local `DockLayoutHandle` declares only `find()` — not rc-dock's `saveLayout()`,
 * which is the only member used here and in DesignViewerLayoutManager. Declared locally instead.
 */
export type HoopsViewerHandle = { rcDockref: { saveLayout: () => unknown } };

interface DesignViewerLayoutSettingProps {
  setLayoutSettingMenu: (open: boolean) => void;
  availableData: SidebarGroup[];
  setAvailableData: (data: SidebarGroup[]) => void;
  addedData: SidebarGroup[];
  setAddedData: (data: SidebarGroup[]) => void;
  HoopsViewer: HoopsViewerHandle;
  updateData: ViewerLayout;
  onSaveCallback?: (args: { updateData: ViewerLayout }) => void;
  setUpdateData: (data: ViewerLayout) => void;
}

function DesignViewerLayoutSetting({
  setLayoutSettingMenu,
  availableData,
  setAvailableData,
  addedData,
  setAddedData,
  HoopsViewer,
  updateData,
  onSaveCallback,
  setUpdateData,
}: DesignViewerLayoutSettingProps) {
  const { t } = useTranslation();
  const [zoomLevel, setZoomLevel] = useState<number | null>(null);

  useEffect(() => {
    if (updateData) {
      setZoomLevel(updateData.data.zoomLevel);
    }
  }, [updateData]);

  function panelHeader() {
    return (
      <div className="flex items-center gap-3 text-base font-bold text-[var(--primary-text)] ps-7">
        <IconComponent name="layoutsIcon" height="1.5rem" width="1.5rem" />
        <div>{t("Panels")}</div>
      </div>
    );
  }

  const panelsContent = () => (
    <DesignViewerLayoutSettingComponent
      availableData={availableData}
      setAvailableData={setAvailableData}
      addedData={addedData}
      setAddedData={setAddedData}
    />
  );

  const settingAccordion = () => (
    <div className="flex flex-col h-full overflow-auto">
      <div className="flex items-center py-5 ps-10 flex-none">
        <div className="pe-[3.2%]">{t("Layout Name")}</div>
        <div className="w-[36%]">
          <TextFieldComponent
            type="text"
            value={updateData.name}
            onChange={(args: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              setUpdateData({ ...updateData, name: args.target.value });
            }}
          />
        </div>
      </div>
      <div className="h-full overflow-hidden">
        <Accordion
          defaultExpanded
          className="h-full"
          sx={{
            "& .MuiCollapse-root": {
              display: "flex",
              overflow: "auto",
              height: "90%",
            },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>{panelHeader()}</AccordionSummary>
          <AccordionDetails>{panelsContent()}</AccordionDetails>
        </Accordion>
      </div>
      {/* <div className="flex-auto">
         <Accordion defaultExpanded>
           <AccordionSummary expandIcon={<ExpandMoreIcon />}>
             {zoomHeader()}
           </AccordionSummary>
           <AccordionDetails>
             {zoomContent()}
           </AccordionDetails>
         </Accordion>
       </div> */}
      <div className="flex h-20 items-center justify-end gap-[2.4%] p-2.5 pe-[4%] flex-none">
        <ButtonComponent buttonType="borderButton" onClick={onCancel}>
          {t("Cancel")}
        </ButtonComponent>
        <ButtonComponent buttonType="gradientButton" onClick={onSave}>
          {t("Save")}
        </ButtonComponent>
      </div>
    </div>
  );
  async function onSave() {
    const addData: string[] = [];
    addedData.map((data: SidebarGroup) => {
      addData.push(...(Object.values(data)[0] as { widgets: string[] }).widgets);
    });
    updateData.data.sidebarLayout.addedData = addData;
    updateData.data.zoomLevel = zoomLevel;
    // stringify and parse to break refrence
    updateData.data.rcDock = JSON.parse(JSON.stringify(HoopsViewer.rcDockref.saveLayout()));
    const response = await axiosWrapper.post<UpdateViewResponse>(`${getBackendUrl()}/layoutView/updateView`, {
      updateData: {
        name: updateData.name,
        data: updateData.data,
      },
      id: updateData._id,
    });
    if (response.data.status) {
      onSaveCallback && onSaveCallback({ updateData });
    }
    setLayoutSettingMenu(false);
  }

  function onCancel() {
    setLayoutSettingMenu(false);
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-[rgb(25_32_72_/_50%)] z-[99]">
      <div className="absolute w-[52.2%] h-[86.5%] bg-[var(--surface-white)] text-[var(--primary-text)] min-w-[40.625rem] flex flex-col z-[100] overflow-hidden">
        <div className="flex items-center justify-between bg-[var(--surface-stroke-gradient-color1)] h-15 text-2xl font-bold px-10 py-3 sticky top-0 z-10">
          <div>{t("Layout Settings")}</div>
          <IconComponent name="close" height="1.5rem" width="1.5rem" onClick={onCancel} />
        </div>
        {settingAccordion()}
      </div>
    </div>
  );
}

export default DesignViewerLayoutSetting;
